import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiMenu, FiX, FiCode } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import ThemeToggle from './ui/ThemeToggle';

// Links with href are hash-scroll anchors on the home page.
// Links with `to` are full page routes.
const navLinks = [
  { href: '#about', label: 'About' },
  { href: '#skills', label: 'Skills' },
  { href: '#projects', label: 'Projects' },
  { href: '#experience', label: 'Experience' },
  { href: '#contact', label: 'Contact' },
  { to: '/blog', label: 'Blog' },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const showLoginLink = !user && location.pathname === '/supersecret';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleLogoClick = () => {
    setMenuOpen(false);
    if (location.pathname === '/') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleNavClick = (href) => {
    setMenuOpen(false);
    if (location.pathname === '/') {
      // Small delay lets the mobile menu close animation finish before scrolling
      setTimeout(() => {
        const el = document.querySelector(href);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 50);
    } else {
      navigate({ pathname: '/', hash: href });
    }
  };

  return (
    <motion.nav
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        height: 'var(--nav-height)',
        display: 'flex',
        alignItems: 'center',
        padding: '0 24px',
        background: scrolled ? 'var(--nav-bg-scrolled)' : 'transparent',
        backdropFilter: scrolled ? 'blur(20px)' : 'none',
        WebkitBackdropFilter: scrolled ? 'blur(20px)' : 'none',
        borderBottom: scrolled ? '1px solid var(--border-color)' : 'none',
        boxShadow: scrolled ? '0 4px 24px rgba(0,0,0,0.4)' : 'none',
        transition: 'all 0.3s ease',
      }}
    >
      <div className="container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', maxWidth: '1200px', margin: '0 auto' }}>
        {/* Logo */}
        <Link to="/" onClick={handleLogoClick} style={{ display: 'flex', alignItems: 'center', gap: '10px', textDecoration: 'none' }}>
          <div style={{
            width: 38, height: 38,
            background: 'var(--gradient-primary)',
            borderRadius: '10px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <FiCode size={20} color="white" />
          </div>
          <span className="nav-logo-text" style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: '1.1rem', color: 'var(--text-primary)' }}>
            edemaukabi<span style={{ color: 'var(--accent-primary)' }}>.dev</span>
          </span>
        </Link>

        {/* Desktop Nav */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }} className="desktop-nav">
          {navLinks.map(link => {
            const isActive = link.to && location.pathname === link.to;
            const sharedStyle = {
              fontSize: '0.9rem', fontWeight: 500, padding: '8px 16px', borderRadius: '8px',
              transition: 'all 0.2s', fontFamily: 'var(--font-sans)', textDecoration: 'none',
              color: isActive ? 'var(--accent-primary)' : 'var(--text-secondary)',
              background: isActive ? 'rgba(108,99,255,0.08)' : 'none',
            };
            return link.to ? (
              <Link
                key={link.to}
                to={link.to}
                style={{ ...sharedStyle, display: 'inline-block' }}
                onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-primary)'; if (!isActive) e.currentTarget.style.background = 'rgba(108,99,255,0.08)'; }}
                onMouseLeave={e => { e.currentTarget.style.color = isActive ? 'var(--accent-primary)' : 'var(--text-secondary)'; if (!isActive) e.currentTarget.style.background = 'none'; }}
              >
                {link.label}
              </Link>
            ) : (
              <button
                key={link.href}
                onClick={() => handleNavClick(link.href)}
                style={{ ...sharedStyle, background: 'none', border: 'none', cursor: 'pointer' }}
                onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-primary)'; e.currentTarget.style.background = 'rgba(108,99,255,0.08)'; }}
                onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-secondary)'; e.currentTarget.style.background = 'none'; }}
              >
                {link.label}
              </button>
            );
          })}
          <ThemeToggle />
          {user ? (
            <div style={{ display: 'flex', gap: '8px', marginLeft: '8px' }}>
              <Link to="/supersecret" className="btn btn-outline" style={{ padding: '8px 20px', fontSize: '0.85rem' }}>Dashboard</Link>
              <button onClick={logout} className="btn btn-primary" style={{ padding: '8px 20px', fontSize: '0.85rem' }}>Logout</button>
            </div>
          ) : showLoginLink ? (
            <Link to="/login" className="btn btn-primary" style={{ marginLeft: '8px', padding: '8px 24px', fontSize: '0.88rem' }}>Login</Link>
          ) : null}
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          style={{ display: 'none', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-primary)', padding: '8px' }}
          className="mobile-menu-btn"
        >
          {menuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            style={{
              position: 'absolute', top: '100%', left: 0, right: 0,
              background: 'var(--bg-primary)',
              backdropFilter: 'blur(20px)',
              borderBottom: '1px solid var(--border-color)',
              padding: '16px 24px',
              display: 'flex', flexDirection: 'column', gap: '8px',
            }}
          >
            {navLinks.map(link => {
              const isActive = link.to && location.pathname === link.to;
              const mobileStyle = {
                fontSize: '1rem', textAlign: 'left', padding: '10px 16px', borderRadius: '8px',
                fontFamily: 'var(--font-sans)', textDecoration: 'none',
                color: isActive ? 'var(--accent-primary)' : 'var(--text-secondary)',
                background: isActive ? 'rgba(108,99,255,0.08)' : 'none',
              };
              return link.to ? (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setMenuOpen(false)}
                  style={{ ...mobileStyle, display: 'block' }}
                >
                  {link.label}
                </Link>
              ) : (
                <button
                  key={link.href}
                  onClick={() => handleNavClick(link.href)}
                  style={{ ...mobileStyle, border: 'none', cursor: 'pointer', width: '100%' }}
                >
                  {link.label}
                </button>
              );
            })}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px' }}>
              <ThemeToggle />
            </div>
            {user ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
                <Link to="/admin" onClick={() => setMenuOpen(false)} className="btn btn-outline" style={{ textAlign: 'center' }}>Dashboard</Link>
                <button onClick={() => { logout(); setMenuOpen(false); }} className="btn btn-primary">Logout</button>
              </div>
            ) : showLoginLink ? (
              <Link to="/login" className="btn btn-primary" onClick={() => setMenuOpen(false)} style={{ marginTop: '8px', textAlign: 'center' }}>Login</Link>
            ) : null}
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        @media (max-width: 768px) {
          .desktop-nav { display: none !important; }
          .mobile-menu-btn { display: flex !important; }
        }
      `}</style>
    </motion.nav>
  );
};

export default Navbar;
